import { de } from './de.js';
import { en } from './en.js';
import type { MessageKey, Messages } from './en.js';
import { es } from './es.js';
import { fr } from './fr.js';
import { nl } from './nl.js';
import { ru } from './ru.js';
import { zh } from './zh.js';

export type { MessageKey } from './en.js';

export const SUPPORTED_LOCALES = ['en', 'ru', 'de', 'es', 'nl', 'fr', 'zh'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const CATALOGUES: Readonly<Record<Locale, Messages>> = { en, ru, de, es, nl, fr, zh };

const isSupported = (value: string): value is Locale => (SUPPORTED_LOCALES as readonly string[]).includes(value);

/** First supported language in the user's preference list, matched on the primary subtag; English otherwise. */
export const pickLocale = (languages: readonly string[]): Locale => {
  for (const tag of languages) {
    const primary = tag.toLowerCase().split('-')[0] ?? '';
    if (isSupported(primary)) {
      return primary;
    }
  }
  return 'en';
};

export type Params = Readonly<Record<string, string | number>>;

/** Replaces `{name}` placeholders; unknown placeholders are left visible so missing params show up in review. */
export const interpolate = (template: string, params: Params): string =>
  template.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match));

export type Translator = (key: MessageKey, params?: Params) => string;

export const createTranslator = (locale: Locale): Translator => {
  const messages = CATALOGUES[locale];
  return (key, params = {}) => interpolate(messages[key], params);
};

export const detectLocale = (nav: Pick<Navigator, 'languages' | 'language'>): Locale =>
  pickLocale(nav.languages.length > 0 ? nav.languages : [nav.language]);

/** Resolved once at start-up from the browser's language preferences. */
export const locale: Locale = detectLocale(navigator);

export const t: Translator = createTranslator(locale);
